// The app's keyboard submit policy, in one place (UI/UX audit **F-02**).
//
// **A policy, not a component.** The audit's first draft proposed unifying the Beekeeper chat input
// and the sandbox prompt into one shared control on the grounds that they are "the same control in
// two places". They are not: `chat-input` lives in a modal overlay with a busy/stop lifecycle
// (`ChatOverlay.tsx`), while `sandbox-prompt-input` is an inline auto-growing textarea in a pinned
// bar with a scope chip (`SandboxScreen.tsx`). Coupling them would be a far bigger change than the
// defect warrants. What they genuinely share is the *decision* — which keystroke means "send" — so
// that is the only thing extracted here.
//
// **Why this is not applied to every textarea.** Two of the app's textareas are B++ **code editors**
// (`CodeView.tsx`'s `code-edit-draft` and `TypeSheet.tsx`'s `type-sheet-edit-draft`). In an editor
// Enter must insert a newline, so a blanket "Enter submits" sweep would break them. Keeping the rule
// explicit at each call site is what makes that impossible to do by accident.

import type { KeyboardEvent } from 'react';

/**
 * An `onKeyDown` handler implementing **Enter submits, Shift+Enter inserts a newline**.
 *
 * Three guards, each load-bearing:
 *
 *   * **`shiftKey`** — the escape hatch for a deliberate newline, which is the behaviour a
 *     multi-line prompt still needs.
 *   * **`isComposing`** — an IME's candidate-selection Enter must not send. Without this, typing
 *     Japanese, Chinese or Korean fires a send mid-word on the first candidate commit. Read from
 *     `nativeEvent` because React's synthetic event does not surface it.
 *   * **`altKey`/`ctrlKey`/`metaKey`** — a modified Enter is someone else's shortcut (or a
 *     newline convention from another app), never this one's submit.
 *
 * **`submit` reports whether it actually submitted, and the event is consumed only if it did**
 * (review follow-up). The first version called `preventDefault()` unconditionally and then invoked
 * `submit`, which made the policy correct only for callers whose `submit` has no guards of its own —
 * and neither caller is. `SandboxScreen`'s repeats the Send button's conditions, so with a practice
 * lock up (where the textarea is *not* disabled) Enter did nothing at all: no send, and no newline
 * either, because the event had already been swallowed. That is a dead key, and every future guard a
 * caller adds would silently create another one.
 *
 * Returning a boolean keeps the rule local and checkable: **consume what you handled, nothing else.**
 * An Enter the caller declines keeps its native behaviour.
 */
export function submitOnEnter(
  submit: () => boolean,
): (event: KeyboardEvent<HTMLTextAreaElement | HTMLInputElement>) => void {
  return (event) => {
    if (event.key !== 'Enter') {
      return;
    }
    if (event.shiftKey || event.altKey || event.ctrlKey || event.metaKey) {
      return;
    }
    // `isComposing` is on the native event only.
    if (event.nativeEvent.isComposing) {
      return;
    }
    // Ask first, consume second.
    if (submit()) {
      event.preventDefault();
    }
  };
}
